// Wallet Ops → Custody — the custody wallet directory from
// crypto-wallet-address-service (SM-211). Addresses, types, backends,
// statuses; never key material. Paginated with network / type filters.
//
// Balances (SM-245) come from a second upstream: address-service knows
// the wallets, crypto-wallet-service knows the assets and the RPC
// endpoints. The two are joined here on (network_id, address) — never on
// symbol, because two networks can each configure a "USDC" (SM-519).
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { useCanConfig } from "@/components/wallet-ops/configShared";
import {
  CustodyBalancesCell,
  custodyBalanceKey,
} from "@/components/wallet-ops/CustodyBalances";
import { ProvisionSystemWalletDialog } from "@/components/wallet-ops/ProvisionSystemWalletDialog";
import { usePagination } from "@/lib/usePagination";
import {
  fetchCryptoNetworks,
  fetchCustodyBalances,
  fetchCustodyWallets,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CustodyBalance } from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/wallet-ops/custody")({
  component: CustodyPage,
});

const th =
  "px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted";
const td = "px-3 py-3 text-xs";

function typeTone(t: string): "accent" | "warning" | "success" | "muted" {
  switch (t) {
    case "HOT":
      return "warning";
    case "COLD":
      return "success";
    case "GAS":
      return "accent";
    default:
      return "muted";
  }
}

// Flags a custody wallet whose network is retired or switched off: deposits
// to it aren't scanned/credited and withdrawals from it are blocked. Nothing
// renders for a live network, or before the network list has loaded (absent
// from the map) — we only call out a definitely-not-live network.
function NetworkStateBadge({
  state,
}: {
  state?: { enabled: boolean; archived: boolean };
}) {
  if (!state) return null;
  if (state.archived)
    return (
      <Badge tone="muted" title="Network archived in Configuration → Networks">
        ARCHIVED
      </Badge>
    );
  if (!state.enabled)
    return (
      <Badge tone="muted" title="Network disabled in Configuration → Networks">
        DISABLED
      </Badge>
    );
  return null;
}

function CustodyPage() {
  const [networkID, setNetworkID] = useState("");
  const [walletType, setWalletType] = useState("");
  const [provisionOpen, setProvisionOpen] = useState(false);
  const canConfig = useCanConfig();
  const { page, setPage, limit, offset } = usePagination();

  useEffect(() => {
    setPage(0);
  }, [networkID, walletType]); // eslint-disable-line react-hooks/exhaustive-deps

  const wallets = useQuery({
    queryKey: walletKeys.custody(
      networkID.trim() || undefined,
      walletType || undefined,
      limit,
      offset,
    ),
    queryFn: () =>
      fetchCustodyWallets({
        network_id: networkID.trim() || undefined,
        wallet_type: walletType || undefined,
        limit,
        offset,
      }),
  });

  // Network state so a custody wallet on a disabled/archived network is
  // flagged as such — deposits to it won't be scanned or credited, and
  // withdrawals from it are blocked, so an operator scanning custody needs
  // to see that. include_archived=true so a retired network's wallets are
  // still recognised (they linger after archive). Keyed by network_id.
  const netState = useQuery({
    queryKey: walletKeys.networksList(true),
    queryFn: () => fetchCryptoNetworks(true),
  });
  const netByID = useMemo(() => {
    const m = new Map<string, { name: string; enabled: boolean; archived: boolean }>();
    for (const n of netState.data ?? [])
      m.set(n.network_id, { name: n.name, enabled: n.enabled, archived: !!n.archived });
    return m;
  }, [netState.data]);

  // Balances are read per network (the RPC endpoint is per network), and
  // only for the page on screen — one snapshot per distinct network, not
  // one request per row.
  const addressesByNetwork = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const w of wallets.data?.items ?? []) {
      const list = m.get(w.network_id);
      if (list) list.push(w.address);
      else m.set(w.network_id, [w.address]);
    }
    return m;
  }, [wallets.data]);
  const networks = useMemo(
    () => [...addressesByNetwork.keys()],
    [addressesByNetwork],
  );

  const balanceQueries = useQueries({
    queries: networks.map((n) => {
      const addrs = addressesByNetwork.get(n) ?? [];
      return {
        queryKey: walletKeys.custodyBalances(n, addrs),
        queryFn: () => fetchCustodyBalances(n, addrs),
        // Chain reads are the slow, rate-limited part; don't re-run them
        // while an operator flips back and forth between filters.
        staleTime: 15_000,
      };
    }),
  });

  // networks[i] ↔ balanceQueries[i] — useQueries preserves input order.
  const balances = useMemo(() => {
    const byAddress = new Map<string, CustodyBalance[]>();
    const loading = new Set<string>();
    const failed = new Map<string, string>();
    networks.forEach((n, i) => {
      const q = balanceQueries[i];
      if (!q) return;
      if (q.isLoading) loading.add(n);
      if (q.isError) failed.set(n, (q.error as Error).message);
      for (const b of q.data?.balances ?? []) {
        // Key on the network the SERVICE declared, not the one we asked
        // for: identity is (network, contract), and a row that says it
        // belongs elsewhere must not land in this network's bucket.
        const k = custodyBalanceKey(b.network_id || n, b.address);
        const list = byAddress.get(k);
        if (list) list.push(b);
        else byAddress.set(k, [b]);
      }
    });
    return { byAddress, loading, failed };
  }, [networks, balanceQueries]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Custody wallets</h1>
          <p className="text-sm text-muted">
            Every custody wallet the address service manages: per-user deposit
            addresses plus the HOT / COLD / GAS system wallets. Key material
            never leaves the signer.
          </p>
        </div>
        {canConfig && (
          <Button onClick={() => setProvisionOpen(true)}>
            Provision system wallet
          </Button>
        )}
      </div>
      <ProvisionSystemWalletDialog
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Input
            placeholder="Network, e.g. eth-sepolia"
            value={networkID}
            onChange={(e) => setNetworkID(e.target.value)}
          />
        </div>
        <div className="w-48">
          <Select
            placeholder="Type — any"
            value={walletType}
            onChange={(e) => setWalletType(e.target.value)}
            options={[
              { value: "DEPOSIT", label: "Deposit" },
              { value: "HOT", label: "Hot" },
              { value: "COLD", label: "Cold" },
              { value: "GAS", label: "Gas" },
            ]}
          />
        </div>
        {(networkID || walletType) && (
          <Button
            variant="ghost"
            onClick={() => {
              setNetworkID("");
              setWalletType("");
            }}
          >
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Custody wallets</CardTitle>
          <span className="text-2xs text-muted">{wallets.data?.page.total ?? 0}</span>
        </CardHeader>
        <CardBody className="p-0">
          {wallets.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {wallets.isError && (
            <p className="px-5 py-16 text-center text-sm text-no">
              {(wallets.error as Error).message}
            </p>
          )}
          {wallets.data && wallets.data.items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {networkID || walletType
                ? "No wallets match the current filters."
                : "No custody wallets yet."}
            </p>
          )}
          {wallets.data && wallets.data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={`${th} px-5`}>Address</th>
                    <th className={th}>Type</th>
                    <th className={th}>Network</th>
                    <th
                      className={th}
                      title="Live on-chain balance of every asset configured on the network, active or not"
                    >
                      Balances
                    </th>
                    <th className={th}>User</th>
                    <th className={th}>Backend</th>
                    <th className={th}>Status</th>
                    <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {wallets.data.items.map((w) => (
                    <tr key={w.wallet_id} className="border-b border-border/60 last:border-0">
                      <td className={`${td} px-5 font-mono text-2xs`}>{w.address}</td>
                      <td className={td}>
                        <Badge tone={typeTone(w.wallet_type)}>{w.wallet_type}</Badge>
                      </td>
                      <td className={td}>
                        <span className="flex items-center gap-1.5" title={w.network_id}>
                          {netByID.get(w.network_id)?.name || w.network_id}
                          <NetworkStateBadge state={netByID.get(w.network_id)} />
                        </span>
                      </td>
                      <td className={td}>
                        <CustodyBalancesCell
                          rows={balances.byAddress.get(
                            custodyBalanceKey(w.network_id, w.address),
                          )}
                          loading={balances.loading.has(w.network_id)}
                          error={balances.failed.get(w.network_id)}
                        />
                      </td>
                      <td className={`${td} font-mono text-2xs`}>
                        {w.user_id ? (
                          <Link
                            to="/users/$id"
                            params={{ id: w.user_id }}
                            title={w.user_id}
                            className="text-neon hover:underline"
                          >
                            {`${w.user_id.slice(0, 6)}…${w.user_id.slice(-6)}`}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={td}>{w.backend}</td>
                      <td className={td}>
                        <Badge tone={w.status === "ACTIVE" ? "success" : "muted"}>
                          {w.status}
                        </Badge>
                      </td>
                      <td className={`${td} px-5 text-right text-2xs text-muted`}>
                        {w.created_at ? fmtRelative(w.created_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {wallets.data && wallets.data.page.total > 0 && (
            <Pagination
              page={wallets.data.page}
              currentPage={page}
              onPageChange={setPage}
              itemLabel="wallets"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
